import { eventListeners } from '@popperjs/core';
import { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  Children,
  isValidElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { usePopper } from 'react-popper';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { Actions } from './Actions.js';
import {
  FeatureHighlightCloseButtonCSS,
  FeatureHighlightPointerArrowCSS,
  FeatureHighlightScrollContainerCSS,
} from './FeatureHighlight.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { devLog } from '../../core/utils/logging.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useKeyboardFocusHandling } from '../../overlays/hooks/useKeyboardFocusHandling.js';
import {
  basePlacements,
  type basePlacementsType,
} from '../../overlays/types/placement.js';
import {
  observeAnimationDone,
  observeOverlaySize,
} from '../../overlays/utils/popper-modifiers.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';

const COMPONENT_NAME = 'FeatureHighlight';

//#region Typings
/**
 * Accepted placement properties for FeatureHighlight
 * @public
 */
export type FeatureHighlightPlacement =
  | 'left-top'
  | 'left-middle'
  | 'left-bottom'
  | 'right-top'
  | 'right-middle'
  | 'right-bottom'
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-middle'
  | 'bottom-right';

/**
 * Interface for the FeatureHighlight component.
 * @public
 */
export interface FeatureHighlightProps
  extends WithChildren,
    AriaLabelingProps,
    DataTestId,
    MaskingProps,
    StylingProps,
    BehaviorTrackingProps {
  /**
   * Whether the FeatureHighlight is currently shown or not. Has to be controlled by the consumer.
   */
  open: boolean;
  /**
   * The element the popover is referring to (i.e. where the pointer is pointing to).
   */
  anchor: HTMLElement | null;
  /**
   * Placement of the FeatureHighlight.
   * @defaultValue 'right-bottom'
   */
  placement?: FeatureHighlightPlacement;
  /**
   * Callback fired when closing the FeatureHighlight.
   */
  onClose: () => void;
}

type FeatureHighlightSlots = {
  title: ReactElement | null;
  visual: ReactElement | null;
  content: ReactElement | null;
  actions: ReactElement | null;
};
//#endregion Typings

const getPreferredPlacement = (
  placement: FeatureHighlightPlacement,
): basePlacementsType => {
  const preferredPlacement = placement.split('-')[0] as basePlacementsType;
  // check if passed placement is actually a valid value, otherwise return the
  if (basePlacements.includes(preferredPlacement)) {
    return preferredPlacement;
  } else {
    return basePlacements[1];
  }
};

/**
 * FeatureHighlight component
 *
 */
const FeatureHighlightRoot = forwardRef<HTMLElement, FeatureHighlightProps>(
  (props, forwardedRef) => {
    const {
      open,
      anchor,
      placement = 'right-bottom',
      onClose,
      children,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;
    const preferredPlacement = getPreferredPlacement(placement);
    const [overlayElement, setOverlayElement] = useState<HTMLElement | null>();
    const [arrowElement, setArrowElement] = useState<HTMLElement | null>();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const featureHighlightContainer = useRef<HTMLElement | null>(null);
    const rootRef = useMergeRefs<HTMLElement | null>([
      setOverlayElement,
      forwardedRef,
    ]);
    const [handleFocusOnKeydown, closedOnKeydown] =
      useKeyboardFocusHandling(closeButtonRef);
    const overlayStacking = useOverlayStacking(undefined, 'feature-highlight');
    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
    const intl = useIntl();
    const slots: FeatureHighlightSlots = {
      title: null,
      visual: null,
      content: null,
      actions: null,
    };

    // featureHighlight should be placed at the end of the DOM but before microguide and notifications
    useLayoutEffect(() => {
      const toastContainer = document.querySelector('[data-toast-container]');
      const microguideContainer = document.querySelector(
        '[data-microguide-container]',
      );
      featureHighlightContainer.current = document.createElement('div');
      featureHighlightContainer.current.setAttribute('aria-live', 'polite');
      featureHighlightContainer.current.setAttribute(
        'data-feature-highlight-container',
        'true',
      );

      if (microguideContainer && microguideContainer.parentNode) {
        microguideContainer.parentNode.insertBefore(
          featureHighlightContainer.current,
          microguideContainer,
        );
      } else if (toastContainer && toastContainer.parentNode) {
        toastContainer.parentNode.insertBefore(
          featureHighlightContainer.current,
          toastContainer,
        );
      } else {
        document.body.appendChild(featureHighlightContainer.current);
      }

      return () => {
        featureHighlightContainer.current?.remove();
      };
    }, []);

    Children.forEach(children, (child) => {
      if (isSlot(child, Title)) {
        slots.title = child;
      } else if (isSlot(child, Visual)) {
        slots.visual = child;
      } else if (isSlot(child, Content)) {
        slots.content = child;
      } else if (isSlot(child, Actions)) {
        slots.actions = child;
      } else if (isValidElement(child)) {
        devLog(
          'Only Title, Visual, Content and Actions are valid children of FeatureHighlight.',
        );
      } else if (child !== null) {
        devLog('The used child is not supported by FeatureHighlight.');
      }
    });

    if (slots.title && ariaLabelingProps['aria-labelledby'] === undefined) {
      ariaLabelingProps['aria-labelledby'] = 'strato-feature-highlight-title';
    }
    if (slots.content && ariaLabelingProps['aria-describedby'] === undefined) {
      ariaLabelingProps['aria-describedby'] =
        'strato-feature-highlight-content';
    }

    // Handle the escape key
    const closeOnEscape = (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closedOnKeydown.current = true;
        onClose();
      }
    };

    const offsetModifier = useMemo(
      () => ({
        name: 'offset',
        options: {
          offset: (({ placement: popperPlacement, popper }) => {
            let skidding = 0;
            const spacingToPopperEdge = 24;
            if (popperPlacement === preferredPlacement) {
              switch (placement) {
                case 'right-bottom':
                case 'left-bottom':
                  skidding = popper.height / 2 - spacingToPopperEdge;
                  break;
                case 'right-top':
                case 'left-top':
                  skidding = -(popper.height / 2 - spacingToPopperEdge);
                  break;
                case 'top-left':
                case 'bottom-left':
                  skidding = -(popper.width / 2 - spacingToPopperEdge);
                  break;
                case 'top-right':
                case 'bottom-right':
                  skidding = popper.width / 2 - spacingToPopperEdge;
                  break;
                default:
                  skidding = 0;
              }
            }
            return [skidding, 16];
          }) as OffsetsFunction,
        },
      }),
      [placement, preferredPlacement],
    );

    const { styles, attributes, update } = usePopper(anchor, overlayElement, {
      placement: preferredPlacement,
      modifiers: [
        offsetModifier,
        {
          name: 'flip',
          options: {
            fallbackPlacements: [...basePlacements],
          },
        },
        {
          name: 'arrow',
          options: {
            element: arrowElement,
            padding: 16,
          },
        },
        observeOverlaySize,
        observeAnimationDone,
        eventListeners,
      ],
    });

    useEffect(() => {
      // Reposition popper when anchor positions change
      if (!anchor || !open || !update) {
        return;
      }

      let prevRect = anchor.getBoundingClientRect();
      let positionPollRequestId: number | null = null;
      let pendingAnimationFrame: number | null = null;
      let isUpdateScheduled = false;

      const scheduleUpdate = () => {
        if (isUpdateScheduled) {
          return;
        }
        isUpdateScheduled = true;

        if (pendingAnimationFrame !== null) {
          cancelAnimationFrame(pendingAnimationFrame);
        }

        pendingAnimationFrame = requestAnimationFrame(async () => {
          await update();
          isUpdateScheduled = false;
          pendingAnimationFrame = null;
        });
      };

      // Poll position changes
      const pollForPositionChange = () => {
        const anchorRect = anchor.getBoundingClientRect();
        const hasPositionChanged =
          Math.abs(anchorRect.x - prevRect.x) > 0.5 ||
          Math.abs(anchorRect.y - prevRect.y) > 0.5 ||
          Math.abs(anchorRect.width - prevRect.width) > 0.5 ||
          Math.abs(anchorRect.height - prevRect.height) > 0.5;

        if (hasPositionChanged) {
          prevRect = anchorRect;
          scheduleUpdate();
        }
        positionPollRequestId = requestAnimationFrame(pollForPositionChange);
      };
      positionPollRequestId = requestAnimationFrame(pollForPositionChange);

      return () => {
        if (positionPollRequestId !== null) {
          cancelAnimationFrame(positionPollRequestId);
        }
        if (pendingAnimationFrame !== null) {
          cancelAnimationFrame(pendingAnimationFrame);
        }
      };
    }, [anchor, open, update]);

    useEffect(() => {
      if (open === false) {
        // handle focus if closed via keyboard
        handleFocusOnKeydown();
      }
    }, [handleFocusOnKeydown, open]);

    return open && anchor
      ? createPortal(
          <Flex
            ref={rootRef}
            onKeyDown={closeOnEscape}
            role="dialog"
            aria-modal="false"
            className={consumerClassName}
            style={{
              ...styles.popper,
              zIndex: overlayStacking.style.zIndex,
              ...consumerStyle,
            }}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...attributes.popper}
            {...ariaLabelingProps}
            {...behavioralTrackingProps}
            {...forwardProps}
            data-dt-component={COMPONENT_NAME}
          >
            <Flex
              className={FeatureHighlightScrollContainerCSS}
              flexDirection="column"
              gap={0}
              paddingX={16}
              paddingTop={8}
              paddingBottom={8}
            >
              <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={8}
                paddingBottom={
                  slots.content !== null ||
                  slots.actions !== null ||
                  slots.visual !== null
                    ? 8
                    : 0
                }
              >
                <Flex
                  flexItem
                  flexGrow={1}
                  paddingY={6}
                  id="strato-feature-highlight-title"
                >
                  {slots.title}
                </Flex>
                <Button
                  className={FeatureHighlightCloseButtonCSS}
                  onClick={() => onClose()}
                  onKeyDown={() => (closedOnKeydown.current = true)}
                  ref={closeButtonRef}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Close feature highlight',
                    id: 'XQHEJyt2t/js0TGr',
                    description: 'Close the feature highlight.',
                  })}
                  data-dt-name="CloseX"
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </Flex>
              {slots.visual && (
                <Flex
                  paddingBottom={
                    slots.content !== null || slots.actions !== null ? 16 : 8
                  }
                >
                  {slots.visual}
                </Flex>
              )}
              {slots.content && (
                <Flex
                  paddingBottom={8}
                  style={{ overflow: 'hidden' }}
                  id="strato-feature-highlight-content"
                >
                  {slots.content}
                </Flex>
              )}
              {slots.actions}
            </Flex>
            <div
              data-popper-arrow
              ref={setArrowElement}
              className={FeatureHighlightPointerArrowCSS}
              style={styles.arrow}
              {...attributes.arrow}
            />
          </Flex>,
          featureHighlightContainer.current || document.body,
        )
      : null;
  },
);

(
  FeatureHighlightRoot as typeof FeatureHighlightRoot & {
    displayName: string;
  }
).displayName = COMPONENT_NAME;

/**
 * The `FeatureHighlight` supports the onboarding of new users, as well as the introduction of new features to existing users.
 * It is positioned next to the specified reference element and has four optional slots: `Title`, `Visual`, `Content` and `Actions`.
 *
 * @public
 */
export const FeatureHighlight = Object.assign(FeatureHighlightRoot, {
  Title,
  Visual,
  Content,
  Actions,
});
